import { datadogRum } from '@datadog/browser-rum';
import React, { useEffect, useRef, useState } from 'react';
import FetchAssignedBucket from '../services';

type Experiments = Record<string, Experiment>;

interface Experiment {
  defaultBucket?: string;
  buckets?: string[];
  isLoading?: boolean;
  hasErrored?: boolean;
  assignedBucket?: string;
  experimentName?: string;
  identifier?: string;
}

const defaultBucket = 'control';

interface BucketId {
  experimentName: string;
  bucketName: string;
}

interface GetAssignedBucketProps {
  experimentName: string;
  possibleBuckets: string[];
  cacheMinutes?: number;
}

const defaultContext = {
  experiments: {} as Experiments,
  UpdateAssignedBucketByExperimentName: (_bucketId: BucketId, _possibleBuckets: string[]) => {
    /* do nothing */
  },
  GetAssignedBucket: (_: GetAssignedBucketProps) => {
    /* do nothing */
  }
};

const ExperimentContext = React.createContext(defaultContext);

const ExperimentProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [experiments, setExperiments] = useState<Experiments>({});
  const fetchQueue = useRef<string[]>([]);

  const getExperimentsWithPossibleBuckets = (
    experimentsToUpdate: Experiments,
    addBuckets: string[],
    experimentName: string
  ) => {
    return {
      ...experimentsToUpdate,
      [experimentName]: {
        ...experimentsToUpdate[experimentName],
        experimentName,
        buckets: Array.from(
          new Set(
            (experimentName in experimentsToUpdate
              ? experimentsToUpdate[experimentName].buckets ?? []
              : []
            ).concat(addBuckets, defaultBucket)
          )
        )
      }
    };
  };

  const getUpdatedExperimentsWithAssignment = (
    experimentsToUpdate: Experiments,
    bucketName: string,
    experimentName: string
  ) => {
    return {
      ...experimentsToUpdate,
      [experimentName]: {
        ...experimentsToUpdate[experimentName],
        assignedBucket: bucketName
      }
    };
  };

  const UpdateAssignedBucketByExperimentName: typeof defaultContext.UpdateAssignedBucketByExperimentName =
    ({ bucketName, experimentName }, possibleBuckets = []) => {
      setExperiments(currExperiments =>
        getUpdatedExperimentsWithAssignment(
          getExperimentsWithPossibleBuckets(
            currExperiments,
            [...possibleBuckets, defaultBucket],
            experimentName
          ),
          bucketName,
          experimentName
        )
      );
    };

  const removeFromQueue = (experimentName: string) =>
    fetchQueue.current.filter(x => x !== experimentName);

  const GetAssignedBucket: typeof defaultContext.GetAssignedBucket = ({
    experimentName,
    possibleBuckets = [],
    cacheMinutes
  }) => {
    if (fetchQueue.current.includes(experimentName)) {
      // already fetching, ignore
    } else {
      fetchQueue.current.push(experimentName);

      Promise.resolve(FetchAssignedBucket({ experimentName, cacheMinutes }))
        .then(response => {
          const bucketName = response.assignedBucket ?? defaultBucket;
          const hasErrored = 'hasErrors' in response && response.hasErrors;
          removeFromQueue(experimentName);

          // Use updater function to derive next experiment state
          setExperiments(currentExperimentState => {
            const updated = getUpdatedExperimentsWithAssignment(
              getExperimentsWithPossibleBuckets(
                currentExperimentState,
                [...possibleBuckets, defaultBucket],
                experimentName
              ),
              bucketName,
              experimentName
            );
            return {
              ...updated,
              [experimentName]: {
                ...updated[experimentName],
                hasErrored
              }
            };
          });

          return bucketName;
        })
        .catch(() => {
          removeFromQueue(experimentName);
          // Set hasErrored so consumers can fallback appropriately
          setExperiments(currentExperimentState => ({
            ...currentExperimentState,
            [experimentName]: {
              ...currentExperimentState[experimentName],
              hasErrored: true
            }
          }));
        });
    }
  };

  useEffect(() => {
    const assignedBuckets: Record<string, string> = {};
    for (const [name, experiment] of Object.entries(experiments)) {
      if (experiment.assignedBucket) {
        assignedBuckets[name] = experiment.assignedBucket;
      }
    }
    datadogRum.setGlobalContextProperty('experiments', assignedBuckets);
  }, [experiments]);

  const value = {
    experiments: experiments,
    UpdateAssignedBucketByExperimentName: UpdateAssignedBucketByExperimentName,
    GetAssignedBucket: GetAssignedBucket
  };

  return <ExperimentContext.Provider value={value}>{children}</ExperimentContext.Provider>;
};

export default ExperimentContext;

export { ExperimentProvider };
